/**
 * ONE rule for "is this time-off range possible", shared by the request modal, the edit modal and
 * the page's submit handlers (board #1945, #1947).
 *
 * Both modals accepted an end BEFORE the start and saved it. Nothing downstream coped: the day
 * count in Timesheets is a `while (currentDate <= endDate)` walk, so an inverted range counts ZERO
 * business days, writes hours_requested = 0, and the list then renders Days as `-`. The request
 * looked submitted and reserved nothing.
 *
 * WHY ONE FUNCTION AND NOT A CHECK IN EACH MODAL: the two modals already drifted once — they build
 * their startsAt/endsAt strings independently — and a validation rule copied into both is the shape
 * this codebase keeps rediscovering (see the phone message that lived in five files). The DATETIME
 * comparison lives here; the modals only render what it returns.
 *
 * COMPARES FULL DATETIMES, not dates and times separately. That is what makes one rule cover both
 * findings: an end date before the start date and an end time before the start time on the same
 * day are the same defect at different resolutions. It also gets the case a split rule gets wrong
 * — a part-day request that legitimately spans midnight-to-midnight across two days, where the end
 * TIME is earlier than the start time but the range is still forward.
 *
 * Returns a human-readable message, or null when the range is fine.
 */

/** Build the exact ISO strings the modals submit, so validation tests what is actually saved. */
export const buildTimeOffRange = ({ startDate, endDate, isFullDay, startTime, endTime }) => ({
  startsAt: isFullDay ? `${startDate}T00:00:00` : `${startDate}T${startTime}:00`,
  endsAt: isFullDay ? `${endDate}T23:59:59` : `${endDate}T${endTime}:00`,
});

export const validateTimeOffRange = ({ startDate, endDate, isFullDay = true, startTime, endTime }) => {
  if (!startDate) return 'Choose a start date.';
  if (!endDate) return 'Choose an end date.';

  if (!isFullDay) {
    if (!startTime) return 'Choose a start time.';
    if (!endTime) return 'Choose an end time.';
  }

  const { startsAt, endsAt } = buildTimeOffRange({ startDate, endDate, isFullDay, startTime, endTime });
  const start = new Date(startsAt);
  const end = new Date(endsAt);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'That date is not one we can read — please re-enter it.';
  }

  // Reported against the field the user would change, and at the resolution they are working in:
  // telling someone editing times that their "dates" are wrong sends them to the wrong control.
  if (end < start) {
    return startDate === endDate
      ? 'The end time is before the start time.'
      : 'The end date is before the start date.';
  }

  // Equal is not a range. A part-day request from 13:00 to 13:00 reserves nothing, and would land
  // in the same zero-hours state as an inverted one.
  if (!isFullDay && end.getTime() === start.getTime()) {
    return 'The start and end times are the same, so no time would be booked.';
  }

  return null;
};

export default validateTimeOffRange;

/**
 * ONE rule for "how many days off is this range" (board #5321, #5324).
 *
 * Timesheets counted weekdays only; the Employees Time Off panel counted every calendar day,
 * so a Fri→Sat request wrote 16h from one screen and 8h from the other into the SAME
 * `employee_time_off.hours_requested` column — nothing in the row says which rule produced it.
 *
 * THE RULE: a day counts when the company is OPEN that day per `company_settings.business_hours`
 * (day-name keyed, `{ monday: { open, start, end }, … }` — the same table the slot engine reads
 * to decide which days are bookable, so "days off" and "days that could have been booked" agree).
 * With no business hours supplied, every calendar day in the range counts, inclusive.
 *
 * Accepts `YYYY-MM-DD`, the modals' `YYYY-MM-DDTHH:mm:ss` wall-clock strings, or a Date; only
 * the calendar date matters, so the date part is read directly and never shifted by timezone.
 */
export const timeOffDays = (start, end, { businessHours } = {}) => {
  const toParts = (v) => {
    if (v instanceof Date) return [v.getFullYear(), v.getMonth(), v.getDate()];
    const [y, m, d] = String(v || '').slice(0, 10).split('-').map(Number);
    return [y, m - 1, d];
  };
  const [sy, sm, sd] = toParts(start);
  const [ey, em, ed] = toParts(end);
  if (![sy, sm, sd, ey, em, ed].every(Number.isFinite)) return 0;
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const cursor = new Date(sy, sm, sd);
  const last = new Date(ey, em, ed);
  let days = 0;
  while (cursor <= last) {
    const day = businessHours ? businessHours[dayNames[cursor.getDay()]] : null;
    if (!businessHours || !day || day.open !== false) days++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};
